/**
 * StrikeContextMenu.js
 * Mini popover that appears when a strike price cell is clicked.
 * Actions: Buy CE, Sell CE, Buy PE, Sell PE, View Chart.
 */
import React from 'react';
import {
    Popover, Paper, MenuList, MenuItem,
    ListItemIcon, ListItemText, Divider, Typography, Box,
} from '@mui/material';
import TrendingUpIcon    from '@mui/icons-material/TrendingUp';
import TrendingDownIcon  from '@mui/icons-material/TrendingDown';
import BarChartIcon      from '@mui/icons-material/BarChart';
import { alpha } from '@mui/material/styles';
import useOptionChainStore from './useOptionChainStore';

export default function StrikeContextMenu({ anchorEl, strike, onClose, onViewChart }) {
    const open = Boolean(anchorEl);
    const toggleLeg = useOptionChainStore(s => s.toggleLeg);

    const handleAction = (side, action) => {
        // Add to strategy builder with the chosen action
        toggleLeg(strike, side);
        const store = useOptionChainStore.getState();
        store.updateLeg(strike, side, { action });
        onClose();
    };

    if (!strike) return null;

    return (
        <Popover
            open={open}
            anchorEl={anchorEl}
            onClose={onClose}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            transformOrigin={{ vertical: 'top', horizontal: 'center' }}
            PaperProps={{
                elevation: 8,
                sx: {
                    borderRadius: 2,
                    overflow: 'hidden',
                    minWidth: 200,
                    border: '1px solid',
                    borderColor: 'divider',
                },
            }}
        >
            {/* Strike header */}
            <Box sx={{ px: 2, py: 1, bgcolor: 'action.hover' }}>
                <Typography variant='subtitle2' fontWeight={700} letterSpacing={0.5}>
                    Strike {strike.toLocaleString('en-IN')}
                </Typography>
            </Box>

            <Divider />

            <MenuList dense disablePadding>
                {/* CE actions */}
                <MenuItem onClick={() => handleAction('CE', 'BUY')}
                    sx={{ '&:hover': { bgcolor: (t) => alpha(t.palette.success.main, 0.12) } }}>
                    <ListItemIcon><TrendingUpIcon fontSize='small' color='success' /></ListItemIcon>
                    <ListItemText primary='Buy CE' />
                </MenuItem>

                <MenuItem onClick={() => handleAction('CE', 'SELL')}
                    sx={{ '&:hover': { bgcolor: (t) => alpha(t.palette.error.main, 0.12) } }}>
                    <ListItemIcon><TrendingDownIcon fontSize='small' color='error' /></ListItemIcon>
                    <ListItemText primary='Sell CE' />
                </MenuItem>

                <Divider sx={{ my: 0.5 }} />

                {/* PE actions */}
                <MenuItem onClick={() => handleAction('PE', 'BUY')}
                    sx={{ '&:hover': { bgcolor: (t) => alpha(t.palette.success.main, 0.12) } }}>
                    <ListItemIcon><TrendingUpIcon fontSize='small' color='success' /></ListItemIcon>
                    <ListItemText primary='Buy PE' />
                </MenuItem>

                <MenuItem onClick={() => handleAction('PE', 'SELL')}
                    sx={{ '&:hover': { bgcolor: (t) => alpha(t.palette.error.main, 0.12) } }}>
                    <ListItemIcon><TrendingDownIcon fontSize='small' color='error' /></ListItemIcon>
                    <ListItemText primary='Sell PE' />
                </MenuItem>

                <Divider sx={{ my: 0.5 }} />

                {/* Chart */}
                <MenuItem onClick={() => { onViewChart?.(strike); onClose(); }}>
                    <ListItemIcon><BarChartIcon fontSize='small' /></ListItemIcon>
                    <ListItemText primary='View Chart' />
                </MenuItem>
            </MenuList>
        </Popover>
    );
}
